import { Icon, IDropdownOption, Stack, Text } from '@fluentui/react';
import { ConfirmDialog, Dropdown, IconName, Sizes, useTheme } from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { useEngine } from '../../../../aiem/engine/hooks';
import { AIEMError, AIEngine, EngineVersion, ValidEngineType } from '../../../../aiem/engine/types';
import { getUpgradeableVersions, isMajorUpgrade } from './utils';

export type AIEMUpgradeDialogProps = {
  engine?: AIEngine | null;
  hidden: boolean;
  onConfirm: (selectedVersion: string) => unknown;
  onDismiss: () => unknown;
};

export const AIEMUpgradeDialog = ({ engine, hidden, onConfirm, onDismiss }: AIEMUpgradeDialogProps) => {
  const { version, engineType } = engine || {},
    theme = useTheme(),
    { listVersions } = useEngine(),
    [availableVersions, setAvailableVersions] = useState<EngineVersion[] | undefined>(),
    [selectedVersion, setSelectedVersion] = useState<string>(''),
    [getVersionsError, setGetVersionsError] = useState<AIEMError | null>(null),
    getAndSetVersions = useCallback(async () => {
      try {
        const versions = await listVersions(engineType as ValidEngineType, '', true);
        setAvailableVersions(versions);

        // Get upgradeable versions and preselect only if one is explicitly marked as latest
        const upgradeableVersions = getUpgradeableVersions(versions, version);
        const latestOption = upgradeableVersions.find(({ isDefault }) => !!isDefault);
        if (latestOption) setSelectedVersion(latestOption.key as string);
      } catch (error: unknown) {
        setGetVersionsError(error as AIEMError);
      }
    }, [engine, engineType, version]),
    title = `Upgrade from ${version} `,
    handleChange = useCallback(
      (event: any) => {
        const value = event?.target?.value;
        setSelectedVersion(value);
      },
      [selectedVersion]
    ),
    handleDropDownChange = (_e: FormEvent<HTMLDivElement>, { key: value }: any) => {
      handleChange(_e);
      setSelectedVersion(value);
    },
    handleConfirm = () => {
      onConfirm(selectedVersion);
    },
    getOptions = useCallback(() => {
      if (!availableVersions) return [];

      const upgradeableVersions = getUpgradeableVersions(availableVersions, version);
      return upgradeableVersions.map(({ key, name: text }: EngineVersion) => ({ key, text } as IDropdownOption));
    }, [availableVersions, version]),
    content = (
      <Stack>
        <Dropdown
          label="Select the version"
          placeholder="Select a version"
          options={getOptions()}
          selectedKey={selectedVersion}
          onChange={handleDropDownChange}
          disabled={!availableVersions}
        />
        {getVersionsError && (
          <p style={{ color: theme.palette?.red500 }}>
            <Icon iconName={IconName.WarningSolid} /> <span>{getVersionsError.message}</span>
          </p>
        )}
        {isMajorUpgrade(selectedVersion, version) && (
          <Text
            style={{
              background: theme.semanticColors?.messageBarBackgroundWarning,
              borderRadius: `${Sizes.borderRadius}px`,
              padding: `${Sizes.padding}px`,
              marginTop: `${Sizes.cardGap}px`,
              font: `14px/20px Inter`,
            }}
          >
            <p style={{ color: theme.palette?.yellow900 }}>
              <Icon iconName={IconName.WarningSolid} /> This change cannot be undone!
            </p>
            <p>We recommend following these steps before upgrading:</p>
            <ul>
              <li>
                <b>Build MLI models:</b> Before upgrading, run MLI jobs on models that you want to continue to interpret
                in future Driverless AI releases. If an MLI job appears in the list of <b>Interpreted Models</b> in your
                current version, then it is retained after upgrading.
              </li>
              <li>
                <b>Build MOJO pipelines:</b> Before upgrading, <b>build MOJO pipelines</b> on all desired models.
              </li>
            </ul>
          </Text>
        )}
      </Stack>
    );

  useEffect(() => {
    if (engine?.upgradeAvailable) {
      void getAndSetVersions();
    }
  }, [engine]);

  return (
    <ConfirmDialog
      confirmationButtonDisabled={!!getVersionsError || !selectedVersion}
      confirmationButtonText={'Upgrade'}
      hidden={hidden}
      content={content}
      title={title}
      onConfirm={handleConfirm}
      onDismiss={onDismiss}
    />
  );
};
